import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import clsx from 'clsx'

import { CruiseType } from 'constants/cruise'
import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import CruiseItineraryMobileSidebar from './CruiseItineraryMobileSidebar'

const PLACEHOLDER_IMAGE_ID = 'szx0yiy4fy4pm81q9te'

const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`

const SidebarList = styled.div`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset-inline-start: ${rem(15)};
    top: 0;
    bottom: 0;
    border-inline-start: 2px dotted ${props => props.theme.palette.neutral.default.four};
  }
`

const Row = styled.div`
  position: relative;
  padding-inline-start: ${rem(40)};
  padding-bottom: ${rem(4)};

  &:last-child {
    padding-bottom: 0;
  }
`

const SlidingMarker = styled.div`
  position: absolute;
  inset-inline-start: ${rem(15)};
  top: 0;
  transform: translate(-50%, -50%);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(28)};
  height: ${rem(28)};
  border-radius: 50%;
  background: ${props => props.theme.palette.neutral.default.eight};
  border: 2px solid ${props => props.theme.palette.neutral.default.one};
  box-shadow: ${props => props.theme.shadow.flat.small};
  pointer-events: none;
  transition: top 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  [dir="rtl"] & {
    transform: translate(50%, -50%);
  }
`

const ArrowToCard = styled.span`
  position: absolute;
  inset-inline-start: ${rem(30)};
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-block-start: ${rem(6)} solid transparent;
  border-block-end: ${rem(6)} solid transparent;
  border-inline-start: ${rem(7)} solid ${props => props.theme.palette.neutral.default.one};
  z-index: 1;
`

const DayCard = styled(Clickable)`
  position: relative;
  width: 100%;
  min-height: ${rem(100)};
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.S};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &.selected {
    transform: scale(1.02);
    z-index: 1;
    box-shadow: ${props => props.theme.shadow.flat.medium};
  }

  ${mediaHoverable} {
    &:hover {
      box-shadow: ${props => props.theme.shadow.flat.small};
    }
  }
`

const CardImage = styled(Image)`
  position: absolute;
  inset: 0;
  border-radius: ${props => props.theme.borderRadius.S};
  outline: 2px solid transparent;
  opacity: 0.6;
  transition: opacity 0.2s ease, outline-color 0.15s ease;

  &.selected {
    opacity: 1;
    outline-color: ${props => props.theme.palette.brand.primary.normal};
  }

  ${mediaHoverable} {
    ${DayCard}:hover &:not(.selected) {
      opacity: 1;
    }

    ${DayCard}:hover & {
      outline-color: ${props => props.theme.palette.brand.primary.normal};
    }
  }
`

const CardGradient = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(to top, rgb(1 11 26 / 45%) 0%, rgb(1 7 21 / 30%) 70%, #09253300 100%);
  pointer-events: none;
  height: 70%;
  top: 30%;
`

const CardBottom = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: ${rem(16)} ${rem(16)} ${rem(14)};
  pointer-events: none;
`

interface DesktopSidebarProps {
  visibleItems: Array<{ item: App.CruiseItineraryItem; index: number }>
  selectedIndex: number
  onSelectItem: (index: number) => void
  itineraryByDay?: Map<number, App.Cruises.OfferItineraryV2Item>
  offerFallbackImageId?: string
}

function CruiseItineraryDesktopSidebar({
  visibleItems,
  selectedIndex,
  onSelectItem,
  itineraryByDay,
  offerFallbackImageId,
}: DesktopSidebarProps) {
  const intl = useIntl()
  const listRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const [markerTopPx, setMarkerTopPx] = useState(0)

  const updateMarkerPosition = useCallback(() => {
    const list = listRef.current
    const row = rowRefs.current.get(selectedIndex)
    if (!list || !row) {
      return
    }
    const listRect = list.getBoundingClientRect()
    const rowRect = row.getBoundingClientRect()
    setMarkerTopPx(rowRect.top - listRect.top + rowRect.height / 2 + list.scrollTop)
  }, [selectedIndex])

  useEffect(() => {
    updateMarkerPosition()

    const list = listRef.current
    if (!list) {
      return undefined
    }

    const row = rowRefs.current.get(selectedIndex)
    const ro = new ResizeObserver(() => {
      updateMarkerPosition()
    })
    ro.observe(list)
    if (row) {
      ro.observe(row)
    }
    window.addEventListener('resize', updateMarkerPosition)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', updateMarkerPosition)
    }
  }, [selectedIndex, updateMarkerPosition, visibleItems])

  const setRowRef = useCallback((index: number, el: HTMLDivElement | null) => {
    const map = rowRefs.current
    if (el) {
      map.set(index, el)
    } else {
      map.delete(index)
    }
  }, [])

  return (
    <SidebarList ref={listRef}>
      <SlidingMarker style={{ top: `${markerTopPx}px` }} aria-hidden>
        <LineShipIcon size="S" />
      </SlidingMarker>

      {visibleItems.map(({ item, index }) => {
        const itineraryItem = itineraryByDay?.get(item.startDay)
        const imageId =
          itineraryItem?.attractions[0]?.imageId ??
          itineraryItem?.imageId ??
          offerFallbackImageId ??
          PLACEHOLDER_IMAGE_ID
        const locationLabel =
          item.port?.name ??
          intl.formatMessage({
            id: 'eglatGtb5bLey9o-rWTz',
            defaultMessage: 'At Sea',
            description: 'Itinerary label for days spent at sea during a cruise',
          })
        const isSelected = index === selectedIndex

        return (
          <Row
            key={`sidebar-${item.startDay}-${item.title}-${index}`}
            ref={el => {
              setRowRef(index, el)
            }}
          >
            {isSelected && <ArrowToCard aria-hidden />}
            <DayCard
              onClick={() => {
                onSelectItem(index)
              }}
              className={clsx({ selected: isSelected })}
            >
              <CardImage
                id={imageId}
                alt={locationLabel}
                fit="center"
                objectFit="cover"
                width={560}
                height={240}
                className={clsx({ selected: isSelected })}
              />
              <CardGradient />
              <CardBottom>
                <Group direction="vertical" gap={4}>
                  <BodyText variant="large" colour="neutral-eight" weight="bold">
                    <FormattedMessage
                      id="7UZX7VsN58ZjEXsFisyN"
                      defaultMessage="Day {day}"
                      description="Day number label in trip itinerary item location summary"
                      values={{ day: item.startDay }}
                    />
                  </BodyText>
                  <Caption variant="large" colour="neutral-eight">
                    {locationLabel}
                  </Caption>
                </Group>
              </CardBottom>
            </DayCard>
          </Row>
        )
      })}
    </SidebarList>
  )
}

interface SidebarProps {
  itinerary: Array<App.CruiseItineraryItem>
  cruiseType: App.CruiseType
  selectedIndex: number
  onSelectItem: (index: number) => void
  itineraryByDay?: Map<number, App.Cruises.OfferItineraryV2Item>
  offerFallbackImageId?: string
}

function CruiseItinerarySidebar({
  itinerary,
  cruiseType,
  selectedIndex,
  onSelectItem,
  itineraryByDay,
  offerFallbackImageId,
}: SidebarProps) {
  const [portsOnly, setPortsOnly] = useState(false)

  const visibleItems = useMemo(() => {
    const entries = itinerary.map((item, index) => ({ item, index }))
    if (!portsOnly) {
      return entries
    }
    return entries.filter(({ item }) => item.port)
  }, [itinerary, portsOnly])

  useEffect(() => {
    if (!portsOnly) {
      return
    }
    const stillVisible = visibleItems.some(({ index }) => index === selectedIndex)
    if (!stillVisible && visibleItems.length > 0) {
      onSelectItem(visibleItems[0].index)
    }
  }, [portsOnly, visibleItems, selectedIndex, onSelectItem])

  const togglePortsOnly = useCallback(() => {
    setPortsOnly(v => !v)
  }, [])

  const sidebarBodyProps = useMemo(
    () => ({
      visibleItems,
      selectedIndex,
      onSelectItem,
      itineraryByDay,
      offerFallbackImageId,
    }),
    [visibleItems, selectedIndex, onSelectItem, itineraryByDay, offerFallbackImageId],
  )

  return (
    <Group direction="vertical" gap={16}>
      {cruiseType === CruiseType.OCEAN && (
        <HeaderRow>
          <CheckboxInput
            id="cruise-itinerary-ports-only"
            size="large"
            onChange={togglePortsOnly}
            checked={portsOnly}
          >
            <FormattedMessage
              id="rQ6uTjk-ekflMnkg6pou"
              defaultMessage="Show ports only"
              description="Checkbox label to filter itinerary to show only ports"
            />
          </CheckboxInput>
        </HeaderRow>
      )}

      <CSSBreakpoint max="mobile">
        <CruiseItineraryMobileSidebar {...sidebarBodyProps} />
      </CSSBreakpoint>

      <CSSBreakpoint min="tablet">
        <CruiseItineraryDesktopSidebar {...sidebarBodyProps} />
      </CSSBreakpoint>
    </Group>
  )
}

export default CruiseItinerarySidebar
